/**
 * Dial detent tables for the Nikon Z fc.
 *
 * Order matters: these arrays are the physical order of the engraved
 * markings, so the UI can render them around a dial and step through them.
 */

import type { ExpCompDialId, IsoDialId, ShutterDialId } from './types'

export interface ShutterDetent {
  id: ShutterDialId
  /** What's engraved on the dial face. */
  engraved: string
  /** Human-readable exposure time. */
  label: string
  /** Duration in seconds; null for B/T/1/3STEP which have no fixed time. */
  seconds: number | null
  /** The 1s/2s/4s markings are printed in red/orange on the real dial. */
  red: boolean
  /** Special positions the dial lock button engages. */
  locking: boolean
}

/**
 * Manual: "1/4000 s–1/2 s and 1–4 s (marked in red)", plus X, T, B, 1/3STEP.
 * The lock button only engages at B, T, X and 1/3STEP.
 */
export const SHUTTER_DETENTS: ShutterDetent[] = [
  { id: '1/3STEP', engraved: '1/3 STEP', label: '1/3 step', seconds: null, red: false, locking: true },
  { id: 'B', engraved: 'B', label: 'Bulb', seconds: null, red: false, locking: true },
  { id: 'T', engraved: 'T', label: 'Time', seconds: null, red: false, locking: true },
  { id: 'X', engraved: 'X', label: 'Flash sync (1/200)', seconds: 1 / 200, red: false, locking: true },
  { id: '1/4000', engraved: '4000', label: '1/4000', seconds: 1 / 4000, red: false, locking: false },
  { id: '1/2000', engraved: '2000', label: '1/2000', seconds: 1 / 2000, red: false, locking: false },
  { id: '1/1000', engraved: '1000', label: '1/1000', seconds: 1 / 1000, red: false, locking: false },
  { id: '1/500', engraved: '500', label: '1/500', seconds: 1 / 500, red: false, locking: false },
  { id: '1/250', engraved: '250', label: '1/250', seconds: 1 / 250, red: false, locking: false },
  { id: '1/125', engraved: '125', label: '1/125', seconds: 1 / 125, red: false, locking: false },
  { id: '1/60', engraved: '60', label: '1/60', seconds: 1 / 60, red: false, locking: false },
  { id: '1/30', engraved: '30', label: '1/30', seconds: 1 / 30, red: false, locking: false },
  { id: '1/15', engraved: '15', label: '1/15', seconds: 1 / 15, red: false, locking: false },
  { id: '1/8', engraved: '8', label: '1/8', seconds: 1 / 8, red: false, locking: false },
  { id: '1/4', engraved: '4', label: '1/4', seconds: 1 / 4, red: false, locking: false },
  { id: '1/2', engraved: '2', label: '1/2', seconds: 1 / 2, red: false, locking: false },
  { id: '1s', engraved: '1', label: '1 sec', seconds: 1, red: true, locking: false },
  { id: '2s', engraved: '2', label: '2 sec', seconds: 2, red: true, locking: false },
  { id: '4s', engraved: '4', label: '4 sec', seconds: 4, red: true, locking: false },
]

export const SHUTTER_BY_ID = new Map(SHUTTER_DETENTS.map((d) => [d.id, d]))

/** The Z fc's flash X-sync speed. */
export const FLASH_SYNC_SECONDS = 1 / 200

/**
 * Range available from the main command dial when the shutter dial sits at
 * 1/3STEP: 1/4000 to 30 s in 1/3-stop increments.
 */
export const THIRD_STEP_RANGE = { fastest: 1 / 4000, slowest: 30 } as const

export interface IsoDetent {
  id: IsoDialId
  engraved: string
  label: string
  value: number
  /** H1/H2 are expansion settings above the native range. */
  extended: boolean
}

export const ISO_DETENTS: IsoDetent[] = [
  { id: '100', engraved: '100', label: 'ISO 100', value: 100, extended: false },
  { id: '200', engraved: '200', label: 'ISO 200', value: 200, extended: false },
  { id: '400', engraved: '400', label: 'ISO 400', value: 400, extended: false },
  { id: '800', engraved: '800', label: 'ISO 800', value: 800, extended: false },
  { id: '1600', engraved: '1600', label: 'ISO 1600', value: 1600, extended: false },
  { id: '3200', engraved: '3200', label: 'ISO 3200', value: 3200, extended: false },
  { id: '6400', engraved: '6400', label: 'ISO 6400', value: 6400, extended: false },
  { id: '12800', engraved: '12800', label: 'ISO 12800', value: 12800, extended: false },
  { id: '25600', engraved: '25600', label: 'ISO 25600', value: 25600, extended: false },
  { id: '51200', engraved: '51200', label: 'ISO 51200', value: 51200, extended: false },
  { id: 'H1', engraved: 'H1', label: 'Hi 1 (~102400)', value: 102400, extended: true },
  { id: 'H2', engraved: 'H2', label: 'Hi 2 (~204800)', value: 204800, extended: true },
]

export const ISO_BY_ID = new Map(ISO_DETENTS.map((d) => [d.id, d]))

/** Exposure compensation detents: −3 to +3 EV in 1/3-stop steps, plus C. */
export const EXP_COMP_DETENTS: ExpCompDialId[] = [
  ...Array.from({ length: 19 }, (_, i) => Math.round((-3 + i / 3) * 100) / 100),
  'C',
]

export function formatExpComp(value: number): string {
  if (Math.abs(value) < 0.005) return '±0'
  const sign = value > 0 ? '+' : '−'
  return `${sign}${Math.abs(value).toFixed(1)} EV`
}

/** Standard 1/3-stop f-number series, used to snap aperture selections. */
export const F_NUMBERS = [
  1.4, 1.6, 1.7, 1.8, 2, 2.2, 2.5, 2.8, 3.2, 3.5, 4, 4.5, 5, 5.6, 6.3, 7.1, 8, 9, 10, 11, 13, 14,
  16, 18, 20, 22, 25, 29, 32, 36, 40,
]

export function snapToFNumber(f: number): number {
  return F_NUMBERS.reduce((best, n) => (Math.abs(n - f) < Math.abs(best - f) ? n : best), F_NUMBERS[0])
}

export function formatAperture(f: number): string {
  return `f/${f % 1 === 0 ? f.toFixed(0) : f.toFixed(1)}`
}

/**
 * Every shutter speed the camera can actually select, in 1/3-stop steps from
 * 1/4000 to 30 s. The camera never lands on an arbitrary value like 1/522 —
 * auto-selected speeds snap to this series.
 */
export const SHUTTER_SERIES: number[] = (() => {
  const denominators = [
    4000, 3200, 2500, 2000, 1600, 1250, 1000, 800, 640, 500, 400, 320, 250, 200, 160, 125, 100, 80,
    60, 50, 40, 30, 25, 20, 15, 13, 10, 8, 6, 5, 4, 3, 2.5, 2, 1.6, 1.3,
  ]
  const wholes = [1, 1.3, 1.6, 2, 2.5, 3, 4, 5, 6, 8, 10, 13, 15, 20, 25, 30]
  return [...denominators.map((d) => 1 / d), ...wholes]
})()

/** Snaps an arbitrary duration to the nearest speed the camera can show. */
export function snapToShutter(seconds: number): number {
  return SHUTTER_SERIES.reduce((best, s) =>
    Math.abs(Math.log2(s) - Math.log2(seconds)) < Math.abs(Math.log2(best) - Math.log2(seconds))
      ? s
      : best,
  )
}

/** Renders a duration in seconds the way the camera displays it. */
export function formatShutter(seconds: number | null): string {
  if (seconds === null) return '—'
  if (seconds >= 1) return `${Number(seconds.toFixed(1))}"`
  const denominator = 1 / seconds
  // Between 1/2.5 and 1 second the camera shows a fractional denominator.
  if (denominator < 10) return `1/${Number(denominator.toFixed(1))}`
  return `1/${Math.round(denominator)}`
}
